import type { Obra } from './modelo.js';
import { normalizar } from './texto.js';

/**
 * Deteccion de posibles duplicados.
 *
 * DICE «PODRIA», NUNCA «ES». No junta ni borra nada: ensena lo que se parece y
 * deja decidir. Dos chalets contiguos del mismo promotor en la misma calle son
 * dos obras distintas que se parecen muchisimo, y un sistema que las fusionara
 * solo por parecerse destruiria trabajo de campo.
 *
 * La comparacion es deliberadamente simple y explicable: normaliza el texto y
 * mira cinco cosas. Nada de distancias de edicion ni umbrales magicos que nadie
 * pueda justificar delante del equipo comercial.
 */

/**
 * Texto comparable: sin acentos, sin puntuacion, sin abreviaturas de via y sin
 * espacios de sobra. «C/ Cami de Son Vic, 14» y «Cami de Son Vic 14» son la
 * misma direccion escrita por dos personas distintas.
 *
 * Los acentos caen sin regla propia: `NFD` separa la letra de su tilde, y el
 * filtro de mas abajo, que solo deja letras y digitos, se lleva la tilde por
 * delante. Una clase de caracteres combinantes escrita a mano en el fichero se
 * rompe en cuanto alguien lo guarda con otra codificacion.
 */

export interface Coincidencia {
  obra: Obra;
  /** Claves de traduccion de los motivos por los que se parece. */
  motivos: string[];
}

interface Candidata {
  nombre: string;
  direccion: string;
  municipio: string;
  zona: string;
  codigo?: string;
  promotor?: string;
}

function promotorDe(obra: Obra): string {
  const c = obra.contactos.find((x) => x.rol === 'PROMOTOR' || x.rol === 'CONSTRUCTORA');
  return c ? normalizar(c.nombre) : '';
}

/**
 * Busca obras que podrian ser la misma que `candidata`.
 *
 * Se exige coincidencia en la ZONA o el MUNICIPIO ademas de otra cosa: sin eso,
 * «Villa Mar» en Soller y «Villa Mar» en El Toro saltarian como duplicado, y a
 * la tercera falsa alarma el aviso deja de leerse.
 */
export function buscarDuplicados(
  candidata: Candidata,
  obras: Obra[],
  excluirId?: string,
): Coincidencia[] {
  const nombre = normalizar(candidata.nombre);
  const direccion = normalizar(candidata.direccion);
  const municipio = normalizar(candidata.municipio);
  const zona = normalizar(candidata.zona);
  const codigo = normalizar(candidata.codigo ?? '');
  const promotor = normalizar(candidata.promotor ?? '');

  const encontradas: Coincidencia[] = [];

  for (const obra of obras) {
    if (obra.id === excluirId) continue;

    const mismaArea =
      (municipio !== '' && normalizar(obra.municipio) === municipio) ||
      (zona !== '' && normalizar(obra.zona) === zona);

    const motivos: string[] = [];

    if (direccion !== '' && normalizar(obra.direccion) === direccion) {
      motivos.push('obras.dup.direccion');
    }
    if (nombre !== '' && normalizar(obra.nombre) === nombre) {
      motivos.push('obras.dup.nombre');
    }
    if (codigo !== '' && normalizar(obra.codigo) === codigo) {
      motivos.push('obras.dup.codigo');
    }
    if (promotor !== '' && promotorDe(obra) === promotor && mismaArea) {
      motivos.push('obras.dup.promotor');
    }

    // Una direccion o un codigo iguales bastan por si solos: son
    // identificadores. El nombre y el promotor solo cuentan dentro de la misma
    // zona, porque se repiten mucho.
    const identifica =
      motivos.includes('obras.dup.direccion') || motivos.includes('obras.dup.codigo');
    const pareceEnLaZona = mismaArea && motivos.length > 0;

    if (identifica || pareceEnLaZona) {
      if (mismaArea) motivos.push('obras.dup.zona');
      encontradas.push({ obra, motivos });
    }
  }

  // Lo que mas se parece, primero.
  return encontradas.sort((a, b) => b.motivos.length - a.motivos.length);
}
